import React, { useEffect, useState } from 'react';
import { X, Loader2, AlertTriangle } from 'lucide-react';
import { Revision, RevisionFiles } from '../../types';
import { getRevisionFiles, getRevisionPdfData } from '../../services/revisions';
import { PDFViewer } from '../PDFPreview/PDFViewer';
import Editor from '@monaco-editor/react';

interface RevisionPreviewProps {
  projectId: string;
  revision: Revision;
  onClose: () => void;
  onRestore: () => Promise<void>;
}

function formatDate(date: Date): string {
  return date.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function RevisionPreview({
  projectId,
  revision,
  onClose,
  onRestore,
}: RevisionPreviewProps) {
  const [files, setFiles] = useState<RevisionFiles | null>(null);
  const [pdfData, setPdfData] = useState<Uint8Array | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFileIndex, setActiveFileIndex] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    Promise.all([
      getRevisionFiles(projectId, revision.id),
      getRevisionPdfData(projectId, revision.id),
    ])
      .then(([filesData, pdf]) => {
        setFiles(filesData);
        setPdfData(pdf);
      })
      .catch((err) => {
        setError(err.message || 'Failed to load revision');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [projectId, revision.id]);

  const texFiles = files?.files.filter((f) => f.type !== 'folder') || [];
  const activeFile = texFiles[activeFileIndex];

  const handleRestoreClick = () => {
    setRestoreError(null);
    setShowConfirm(true);
  };

  const handleConfirmRestore = async () => {
    setRestoring(true);
    setRestoreError(null);
    try {
      await onRestore();
    } catch (err) {
      setRestoreError(err instanceof Error ? err.message : 'Failed to restore revision');
      setRestoring(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="relative flex h-[90vh] w-[95vw] flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        {/* Header */}
        <div className="relative z-10 flex shrink-0 items-center justify-between border-b border-gray-200 px-4 py-3">
          <h2 className="font-semibold text-gray-900">
            Revision from {formatDate(revision.createdAt)}
            {revision.label && (
              <span className="ml-2 text-gray-500 font-normal">
                ({revision.label})
              </span>
            )}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-200 text-gray-500 hover:text-gray-700"
            title="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="relative z-0 flex min-h-0 flex-1 overflow-hidden">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center text-red-600">
              {error}
            </div>
          ) : (
            <>
              {/* Code panel */}
              <div className="flex min-h-0 w-1/2 flex-col border-r border-gray-200">
                {/* File tabs */}
                {texFiles.length > 1 && (
                  <div className="flex border-b border-gray-200 bg-gray-50 overflow-x-auto">
                    {texFiles.map((file, index) => (
                      <button
                        key={file.name}
                        onClick={() => setActiveFileIndex(index)}
                        className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${
                          index === activeFileIndex
                            ? 'bg-white border-b-2 border-blue-600 text-blue-600'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        {file.name}
                      </button>
                    ))}
                  </div>
                )}
                {/* Code viewer / Image preview */}
                <div className="min-h-0 flex-1 overflow-hidden">
                  {activeFile && activeFile.type === 'image' ? (
                    <div className="h-full flex flex-col items-center justify-center bg-gray-50 p-8">
                      <img
                        src={activeFile.content}
                        alt={activeFile.name}
                        className="max-w-full max-h-[80%] object-contain rounded-lg shadow-md border border-gray-200"
                      />
                      <span className="mt-3 text-sm text-gray-500">{activeFile.name}</span>
                    </div>
                  ) : activeFile ? (
                    <Editor
                      height="100%"
                      defaultLanguage="latex"
                      language="latex"
                      value={activeFile.content}
                      theme="vs-light"
                      options={{
                        readOnly: true,
                        fontSize: 14,
                        fontFamily:
                          "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace",
                        lineNumbers: 'on',
                        minimap: { enabled: false },
                        scrollBeyondLastLine: false,
                        wordWrap: 'on',
                        automaticLayout: true,
                      }}
                    />
                  ) : null}
                </div>
              </div>

              {/* PDF panel */}
              <div className="min-h-0 w-1/2">
                <PDFViewer pdfData={pdfData} />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="relative z-10 flex shrink-0 items-center justify-end gap-3 border-t border-gray-200 px-4 py-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded"
          >
            Cancel
          </button>
          <button
            onClick={handleRestoreClick}
            disabled={loading || !!error}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Restore This Version
          </button>
        </div>

        {/* Confirmation dialog */}
        {showConfirm && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/30">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-6 w-6 text-amber-500 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900">
                    Restore this revision?
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    This will replace your current files with the code from this
                    revision. Your current state was saved on your last compile.
                  </p>
                  {restoreError && (
                    <p className="text-sm text-red-600 mt-2">
                      {restoreError}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowConfirm(false)}
                  disabled={restoring}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmRestore}
                  disabled={restoring}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50 flex items-center gap-2"
                >
                  {restoring && <Loader2 className="h-4 w-4 animate-spin" />}
                  {restoring ? 'Restoring...' : 'Restore'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
